import React, { useMemo, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart, useAuth, useOrders } from '../context';
import Loader from '../components/Loader';
import './PlaceOrder.css';

const initialAddress = {
    fullName: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
};

const PlaceOrder = () => {
    const navigate = useNavigate();
    const { cart, clearCart } = useCart();
    const { user, updateAddress, addAddress } = useAuth();
    const { placeOrder } = useOrders();

    const [address, setAddress] = useState(initialAddress);
    const [addressTouched, setAddressTouched] = useState({});
    const [card, setCard] = useState({
        name: '',
        number: '',
        expiry: '',
        cvv: '',
    });
    const [cardTouched, setCardTouched] = useState({});
    const [placing, setPlacing] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Prefill from saved address if present and form is empty
        const addresses = user?.addresses || user?.address;
        if (addresses && Array.isArray(addresses) && addresses.length > 0) {
            setAddress((prev) => {
                // Only prefill if the form is currently empty
                if (prev.fullName || prev.line1 || prev.city) return prev;

                const addr = addresses.find(a => a.is_default || a.isDefault) || addresses[0];
                return {
                    id: addr.id,
                    fullName: addr.full_name || addr.fullName || '',
                    phone: addr.phone || '',
                    line1: addr.line1 || '',
                    line2: addr.line2 || '',
                    city: addr.city || '',
                    state: addr.state || '',
                    postalCode: addr.postal_code || addr.postalCode || '',
                    country: addr.country || '',
                };
            });
        }
    }, [user]);

    useEffect(() => {
        // Prefill cardholder name from user profile if empty
        if (user?.name) {
            setCard((prev) => {
                if (prev.name) return prev;
                return { ...prev, name: user.name };
            });
        }
    }, [user]);

    const total = useMemo(
        () => cart.reduce((sum, it) => sum + it.price * it.quantity, 0),
        [cart]
    );

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setAddress((prev) => ({ ...prev, [name]: value }));
    };

    const handleCardChange = (e) => {
        const { name, value } = e.target;

        if (name === 'number') {
            // Keep only digits
            const digits = value.replace(/\D/g, '');
            setCard((prev) => ({ ...prev, number: digits }));
            return;
        }

        if (name === 'expiry') {
            // Allow digits and slash, format as MM/YY
            let v = value.replace(/[^\d/]/g, '');
            if (v.length === 2 && card.expiry.length === 1) v = v + '/';
            if (v.length > 5) v = v.slice(0, 5);
            setCard((prev) => ({ ...prev, expiry: v }));
            return;
        }

        if (name === 'cvv') {
            const digits = value.replace(/\D/g, '').slice(0, 4);
            setCard((prev) => ({ ...prev, cvv: digits }));
            return;
        }

        setCard((prev) => ({ ...prev, [name]: value }));
    };

    const touchAddress = (e) => setAddressTouched((t) => ({ ...t, [e.target.name]: true }));
    const touchCard = (e) => setCardTouched((t) => ({ ...t, [e.target.name]: true }));

    // Validation helpers
    const required = (v) => v != null && String(v).trim().length > 0;

    const isFutureExpiry = (exp) => {
        // Expect MM/YY
        if (!/^\d{2}\/\d{2}$/.test(exp)) return false;
        const [mmStr, yyStr] = exp.split('/');
        const mm = parseInt(mmStr, 10);
        const yy = parseInt(yyStr, 10);
        if (mm < 1 || mm > 12) return false;

        const now = new Date();
        const currentYY = now.getFullYear() % 100;
        const currentMM = now.getMonth() + 1;

        if (yy > currentYY) return true;
        if (yy < currentYY) return false;
        return mm >= currentMM;
    };

    const luhnValid = (num) => {
        if (!/^\d{13,19}$/.test(num)) return false;
        let sum = 0;
        let alt = false;
        for (let i = num.length - 1; i >= 0; i--) {
            let n = parseInt(num[i], 10);
            if (alt) {
                n *= 2;
                if (n > 9) n -= 9;
            }
            sum += n;
            alt = !alt;
        }
        return sum % 10 === 0;
    };

    const addressErrors = {
        fullName: required(address.fullName) ? '' : 'Full name is required',
        phone: required(address.phone) ? '' : 'Phone is required',
        line1: required(address.line1) ? '' : 'Address line 1 is required',
        city: required(address.city) ? '' : 'City is required',
        state: required(address.state) ? '' : 'State/Region is required',
        postalCode: required(address.postalCode) ? '' : 'Postal/ZIP is required',
        country: required(address.country) ? '' : 'Country is required',
    };

    const cardErrors = {
        name: required(card.name) ? '' : 'Name on card is required',
        number: luhnValid(card.number) ? '' : 'Card number is invalid',
        expiry: isFutureExpiry(card.expiry) ? '' : 'Expiry must be MM/YY in the future',
        cvv: /^\d{3,4}$/.test(card.cvv) ? '' : 'CVV must be 3–4 digits',
    };

    const isAddressValid = Object.values(addressErrors).every((e) => !e);
    const isCardValid = Object.values(cardErrors).every((e) => !e);

    const handlePlaceOrder = async () => {
        setError('');

        if (!user) {
            navigate('/login', { state: { from: { pathname: '/checkout' } } });
            return;
        }
        if (cart.length === 0) {
            navigate('/cart');
            return;
        }
        if (!isAddressValid || !isCardValid) {
            // Touch all fields to surface errors
            setAddressTouched({
                fullName: true,
                phone: true,
                line1: true,
                line2: true,
                city: true,
                state: true,
                postalCode: true,
                country: true,
            });
            setCardTouched({
                name: true,
                number: true,
                expiry: true,
                cvv: true,
            });
            setError('Please fix the highlighted fields before placing your order.');
            return;
        }

        try {
            setPlacing(true);

            // Save/update address for the user if the method exists
            try {
                if (address.id && typeof updateAddress === 'function') {
                    await updateAddress(address.id, address);
                } else if (!address.id && typeof addAddress === 'function') {
                    await addAddress(address);
                }
            } catch {
                // Non-blocking; continue placing the order even if address save fails
            }

            // Prepare items for order
            const items = cart.map((it) => ({
                productId: it.productId || it.product_id || it.id,
                name: it.name,
                size: it.size,
                price: it.price,
                quantity: it.quantity,
                imageUrl: it.imageUrl || null,
                comment: it.comment || null,
            }));

            // Note: Card details are validated on the client but never sent or stored
            const order = await placeOrder({ user, items, total });

            clearCart();
            navigate('/account/orders', { replace: true, state: { placed: order.orderNumber || order.id } });
        } catch (e) {
            setError('Failed to place order. Please try again.');
        } finally {
            setPlacing(false);
        }
    };

    // if (cart.length === 0) {
    //     return (
    //         <div className="checkout-empty">
    //             <h2>No items to place</h2>
    //             <Link to="/shop">Go to Shop</Link>
    //         </div>
    //     );
    // }

    return (
        <div className="checkout-container">
            <h2>Review and Place Order</h2>

            <div className="checkout-grid">
                <section className="panel">
                    <h3>Shipping Address</h3>
                    <div className="form-grid">
                        <div className="form-row span-2">
                            <label>Full Name</label>
                            <input
                                name="fullName"
                                value={address.fullName}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="Jane Doe"
                                className="input"
                            />
                            {addressTouched.fullName && addressErrors.fullName && (
                                <div className="field-error">{addressErrors.fullName}</div>
                            )}
                        </div>

                        <div className="form-row span-2">
                            <label>Phone</label>
                            <input
                                name="phone"
                                value={address.phone}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="+1 555 123 4567"
                                className="input"
                            />
                            {addressTouched.phone && addressErrors.phone && (
                                <div className="field-error">{addressErrors.phone}</div>
                            )}
                        </div>

                        <div className="form-row span-2">
                            <label>Address Line 1</label>
                            <input
                                name="line1"
                                value={address.line1}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="123 Main St"
                                className="input"
                            />
                            {addressTouched.line1 && addressErrors.line1 && (
                                <div className="field-error">{addressErrors.line1}</div>
                            )}
                        </div>

                        <div className="form-row span-2">
                            <label>Address Line 2 (optional)</label>
                            <input
                                name="line2"
                                value={address.line2}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="Apt, suite, etc."
                                className="input"
                            />
                        </div>

                        <div className="form-row">
                            <label>City</label>
                            <input
                                name="city"
                                value={address.city}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="City"
                                className="input"
                            />
                            {addressTouched.city && addressErrors.city && (
                                <div className="field-error">{addressErrors.city}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <label>State/Region</label>
                            <input
                                name="state"
                                value={address.state}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="State/Region"
                                className="input"
                            />
                            {addressTouched.state && addressErrors.state && (
                                <div className="field-error">{addressErrors.state}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <label>Postal/ZIP</label>
                            <input
                                name="postalCode"
                                value={address.postalCode}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="ZIP"
                                className="input"
                            />
                            {addressTouched.postalCode && addressErrors.postalCode && (
                                <div className="field-error">{addressErrors.postalCode}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <label>Country</label>
                            <input
                                name="country"
                                value={address.country}
                                onChange={handleAddressChange}
                                onBlur={touchAddress}
                                placeholder="Country"
                                className="input"
                            />
                            {addressTouched.country && addressErrors.country && (
                                <div className="field-error">{addressErrors.country}</div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="panel">
                    <h3>Payment</h3>
                    <div className="form-grid">
                        <div className="form-row span-2">
                            <label>Name on Card</label>
                            <input
                                name="name"
                                value={card.name}
                                onChange={handleCardChange}
                                onBlur={touchCard}
                                placeholder="Jane Doe"
                                autoComplete="cc-name"
                                className="input"
                            />
                            {cardTouched.name && cardErrors.name && (
                                <div className="field-error">{cardErrors.name}</div>
                            )}
                        </div>

                        <div className="form-row span-2">
                            <label>Card Number</label>
                            <input
                                name="number"
                                value={card.number.replace(/(\d{4})(?=\d)/g, '$1 ')}
                                onChange={handleCardChange}
                                onBlur={touchCard}
                                placeholder="4242 4242 4242 4242"
                                inputMode="numeric"
                                autoComplete="cc-number"
                                className="input"
                            />
                            {cardTouched.number && cardErrors.number && (
                                <div className="field-error">{cardErrors.number}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <label>Expiry (MM/YY)</label>
                            <input
                                name="expiry"
                                value={card.expiry}
                                onChange={handleCardChange}
                                onBlur={touchCard}
                                placeholder="MM/YY"
                                inputMode="numeric"
                                autoComplete="cc-exp"
                                className="input"
                            />
                            {cardTouched.expiry && cardErrors.expiry && (
                                <div className="field-error">{cardErrors.expiry}</div>
                            )}
                        </div>

                        <div className="form-row">
                            <label>CVV</label>
                            <input
                                name="cvv"
                                value={card.cvv}
                                onChange={handleCardChange}
                                onBlur={touchCard}
                                placeholder="123"
                                inputMode="numeric"
                                autoComplete="cc-csc"
                                className="input"
                            />
                            {cardTouched.cvv && cardErrors.cvv && (
                                <div className="field-error">{cardErrors.cvv}</div>
                            )}
                        </div>
                    </div>
                    <p className="payment-note">
                        Your card will be securely processed. We do not store payment details.
                    </p>
                </section>
            </div>

            <section className="panel">
                <h3>Order Summary</h3>
                <div className="checkout-items">
                    {cart.map((item) => (
                        <div key={`${item.id}-${item.size || 'na'}-${item.comment || ''}`} className="checkout-item">
                            <div className="checkout-item-left">
                                {item.imageUrl ? (
                                    <img
                                        src={item.imageUrl}
                                        alt={item.name}
                                    />
                                ) : null}
                                <div>
                                    <div className="item-name">{item.name}</div>
                                    {item.size ? <div className="item-size">Size: {item.size}</div> : null}
                                    {item.comment ? <div className="item-comment">Note: {item.comment}</div> : null}
                                    <div className="item-qty">Qty: {item.quantity}</div>
                                </div>
                            </div>
                            <div className="checkout-item-right">
                                ${(item.price * item.quantity).toFixed(2)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="summary-total">
                    <span>Total</span>
                    <strong>${total.toFixed(2)}</strong>
                </div>
            </section>

            {error ? <div className="form-error">{error}</div> : null}

            <div className="checkout-actions">
                <button onClick={() => navigate('/cart')} className="btn btn-secondary">
                    Back to Cart
                </button>
                <button
                    onClick={handlePlaceOrder}
                    className="btn btn-primary"
                    disabled={placing}
                >
                    {placing ? <Loader size="small" color="#fff" /> : 'Place Order'}
                </button>
            </div>
        </div>
    );

};

export default PlaceOrder;